import { type ReactElement, type ReactNode, useCallback } from 'react';
import { QModalActions } from '../../../QComponents';
import { QButtonGroup, QButton, QIconButton } from '../../../QAtoms';
import { FilterDefinitions } from './types';
import { useFilteringForm } from './useForm';
import { FormProvider } from 'react-hook-form';
import {
  Popover,
  PopoverArrow,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger as ChakraPopoverTrigger,
  useDisclosure,
} from '@chakra-ui/react';
import { useLibraryTranslation } from '../../../i18n';

// Re-typed: upstream PopoverTrigger is missing children in its props.
const PopoverTrigger: React.FC<{ children: React.ReactNode }> =
  ChakraPopoverTrigger;

export type FilterPopoverProps = {
  children: ReactNode;
};

export const FilterPopover = <T extends FilterDefinitions>({
  children,
}: FilterPopoverProps): ReactElement => {
  const { t } = useLibraryTranslation();
  const methods = useFilteringForm<T>();
  const { isOpen, onToggle, onClose } = useDisclosure({
    onOpen: methods.reset,
  });

  const clearAllFilters = useCallback(() => {
    for (const key in methods.getValues()) {
      methods.setValue(key, null);
    }
  }, [methods.getValues, methods.setValue]);

  return (
    <FormProvider {...methods}>
      <form onSubmit={methods.handleSubmit(onClose)}>
        <Popover
          isOpen={isOpen}
          onClose={onClose}
          placement="bottom-start"
          closeOnEsc
          closeOnBlur
          autoFocus
        >
          <PopoverTrigger>
            <QIconButton
              aria-label={t('filtering.openModal')}
              iconName="Filter"
              variant="outline"
              onClick={onToggle}
            />
          </PopoverTrigger>
          <PopoverContent
            minWidth="320px"
            width="fit-content"
            _focus={{ boxShadow: 'none' }}
          >
            <PopoverArrow />
            <PopoverBody>{children}</PopoverBody>
            <PopoverFooter>
              <QModalActions>
                <QButtonGroup>
                  <QButton
                    data-cy="clear-filters-button"
                    variant="ghost"
                    onClick={clearAllFilters}
                  >
                    {t('filtering.clear')}
                  </QButton>
                </QButtonGroup>
                <QButton data-cy="apply-filters-button" type="submit">
                  {t('filtering.applyFilters')}
                </QButton>
              </QModalActions>
            </PopoverFooter>
          </PopoverContent>
        </Popover>
      </form>
    </FormProvider>
  );
};
FilterPopover.DisplayName = 'FilterPopover';
